import React, { useState, useRef, useEffect } from 'react';
import { Button } from './button';
import { Input } from './input';
import { Check, X, Edit, Loader2, AlertCircle } from 'lucide-react';

interface InlineEditFieldProps {
  value: string;
  onSave: (newValue: string) => Promise<void>;
  onCancel?: () => void;
  placeholder?: string;
  label?: string;
  type?: 'text' | 'email';
  validation?: (value: string) => string | null; // Return error message or null
  className?: string;
  disabled?: boolean;
  maxLength?: number;
  required?: boolean;
}

export const InlineEditField: React.FC<InlineEditFieldProps> = ({
  value,
  onSave,
  onCancel,
  placeholder,
  label,
  type = 'text',
  validation,
  className = '',
  disabled = false,
  maxLength,
  required = false
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Update input value when prop value changes
  useEffect(() => {
    if (!isEditing) {
      setInputValue(value);
    }
  }, [value, isEditing]);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleEdit = () => {
    if (disabled) return;
    setIsEditing(true);
    setError(null);
    setInputValue(value);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setInputValue(value);
    setError(null);
    onCancel?.();
  };

  const handleSave = async () => {
    const trimmedValue = inputValue.trim();
    
    // Required validation
    if (required && trimmedValue.length === 0) {
      setError('This field is required');
      return;
    }

    // Custom validation
    if (validation) {
      const validationError = validation(trimmedValue);
      if (validationError) {
        setError(validationError);
        return;
      }
    }

    // No change, just exit editing mode
    if (trimmedValue === value) {
      setIsEditing(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await onSave(trimmedValue);
      setIsEditing(false);
    } catch (err: any) {
      setError(err.message || 'Failed to save changes');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  const displayValue = value || placeholder || '';

  if (!isEditing) {
    return (
      <div className={`group ${className}`}>
        {label && (
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {label}
          </label>
        )}
        <div className="relative">
          <div 
            className={`text-sm text-gray-900 bg-gray-50 rounded-md px-3 py-2 min-h-[40px] flex items-center cursor-pointer group-hover:bg-gray-100 transition-colors ${
              disabled ? 'cursor-not-allowed opacity-50' : ''
            }`}
            onClick={handleEdit}
          >
            <span className={value ? '' : 'text-gray-500 italic'}>
              {displayValue}
            </span>
            {!disabled && (
              <Edit className="w-4 h-4 ml-auto text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      <div className="space-y-2">
        <div className="flex items-center space-x-2">
          <Input
            ref={inputRef}
            type={type}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder={placeholder}
            maxLength={maxLength}
            disabled={isLoading}
            className={`flex-1 ${error ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : ''}`}
          />
          <div className="flex items-center space-x-1">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleSave}
              disabled={isLoading}
              className="px-2 py-1 h-8"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Check className="w-4 h-4" />
              )}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleCancel}
              disabled={isLoading}
              className="px-2 py-1 h-8"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
        {error && (
          <div className="flex items-center space-x-1 text-red-600 text-xs">
            <AlertCircle className="w-3 h-3 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </div>
    </div>
  );
};
